import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { HTTPError, TimeoutError } from 'ky';
import type { Geometry, MultiPolygon, Polygon } from 'geojson';
import { api } from 'lib/request/api';
import { capitalizeFirstLetter } from 'utils/strings';
import type {
  AreaGeometry,
  AreaGeometryPart,
  GeometryUpload,
  VerifiedUpload,
} from 'types/area-geometry';
import type { AreaWorkspace } from 'types/area-workspace';
import { useUnitShapeEdit } from 'components/geometry-editing';
import {
  emptySurveyRow,
  useSurveyAreaPick,
  type SurveyRow,
} from 'components/survey-description';
import {
  buildAreaSource,
  useAreaPreview,
  type AreaPreview,
} from 'components/create-area-panel';
import type {
  Bounds,
  ReferenceArea,
  ReferenceCandidate,
  ReferenceMode,
  DraftGeometryEdit,
  DraftGeometrySave,
  EditMethod,
  EditorCompanion,
  EditorSession,
  EditorTool,
  GeometryEditMode,
  Pieces,
  SavedDraftGeometry,
  TraceSource,
} from './types';
import { editorMapRefAtom } from './atoms';
import {
  TOOLS_WIDTH,
  clampToolsWidth,
  editorToolsEnabled,
  inViewCategory,
  geometryAcres,
  geometryToPieces,
  mergePieces,
  piecesBounds,
  piecesToGeometry,
  subtractGeometry,
} from './utils';

/** Said under every failure the server gave no reason for: nothing was lost. */
const EDIT_KEPT = 'Your edit is still here, so you can save again.';

/**
 * Why a draft save failed when the server named no reason: by its status, or
 * how the request itself failed (a timeout, or no connection).
 *
 * @param {unknown} error - The thrown request error.
 * @return {string} What to show.
 */
const unexplainedFailure = (error: unknown): string => {
  if (error instanceof TimeoutError) {
    return `The server took too long to answer. ${EDIT_KEPT}`;
  }
  if (!(error instanceof HTTPError)) {
    return `The server could not be reached. ${EDIT_KEPT}`;
  }
  const { status } = error.response;
  if (status === 404) {
    return 'This workspace’s area was not found. Reopen the workspace and try again.';
  }
  if (status >= 500) {
    return `The server could not save the geometry (error ${status}). ${EDIT_KEPT}`;
  }
  return `The geometry could not be saved (error ${status}). ${EDIT_KEPT}`;
};

/**
 * The reasons a draft save was refused, from the errors envelope, or what is
 * known of the failure when the server gave none.
 *
 * @param {unknown} error - The thrown request error.
 * @return {Promise<string[]>} What to show.
 */
const refusalReasons = async (error: unknown): Promise<string[]> => {
  if (error instanceof HTTPError) {
    const body = (await error.response.json().catch(() => null)) as {
      errors?: { message?: string }[];
    } | null;
    const reasons = (body?.errors ?? [])
      .map((entry) => entry.message)
      .filter((message): message is string => !!message)
      .map(capitalizeFirstLetter);
    if (reasons.length > 0) return reasons;
  }
  return [unexplainedFailure(error)];
};

/**
 * Saves an edit as the workspace's draft: a PATCH to the workspace's own area
 * with the edit's `source` and `mode`. A drawn source carries the whole shape the
 * editor merged, moved and removed parts into; a survey source carries the rows,
 * which the server builds and banks as lineage, as create does. It answers with
 * the stored geometry (repaired when invalid) and its acreage, a 422 naming what
 * is wrong with the shape, or a 409 once the workspace is promoted; promotion
 * later publishes the draft.
 *
 * @param {AreaWorkspace} workspace - The workspace being edited.
 * @return {DraftGeometrySave} The save and its state.
 */
export function useDraftGeometrySave(
  workspace: AreaWorkspace
): DraftGeometrySave {
  const { href } = workspace.detail.area;

  const { mutateAsync, isPending, error, reset } = useMutation<
    SavedDraftGeometry,
    Error & { reasons?: string[] },
    DraftGeometryEdit
  >({
    mutationFn: async ({ source, mode, shown }) => {
      try {
        const saved = await api
          .patch(href.replace(/^\//, ''), { json: { source, mode } })
          .json<Partial<SavedDraftGeometry> | null>();
        // The server's figures win; until it sends them, the shape shown
        // and its measured acres stand in.
        return {
          geometry: saved?.geometry ?? shown,
          grossAcres: saved?.grossAcres ?? geometryAcres(shown),
          parts: saved?.parts,
        };
      } catch (requestError) {
        const failure: Error & { reasons?: string[] } = new Error(
          'Draft save refused'
        );
        failure.reasons = await refusalReasons(requestError);
        throw failure;
      }
    },
    retry: false,
  });

  /**
   * Saves, reporting what landed; the reasons stay on `errors`.
   *
   * @param {DraftGeometryEdit} edit - The edit to save.
   * @return {Promise<SavedDraftGeometry | null>} The saved shape, or null when refused.
   */
  const save = async (edit: DraftGeometryEdit) => {
    try {
      return await mutateAsync(edit);
    } catch {
      return null;
    }
  };

  return {
    save,
    saving: isPending,
    errors: error?.reasons ?? [],
    reset,
  };
}

/**
 * What Save sends for the parts on the map: the survey rows or the file while
 * they are the edit, else the whole shape as drawn, replacing the draft's.
 *
 * @param {Pieces} parts - The parts on the map.
 * @param {Omit<DraftGeometryEdit, 'shown'> | null} addedEdit - The survey rows' or the file's edit, when they are one.
 * @param {Polygon | MultiPolygon | null} original - The area as it opened, whose
 * holes the parts left as they were keep.
 * @return {DraftGeometryEdit | null} The edit, or null with nothing to save.
 */
const editToSave = (
  parts: Pieces,
  addedEdit: Omit<DraftGeometryEdit, 'shown'> | null,
  original: Polygon | MultiPolygon | null
): DraftGeometryEdit | null => {
  if (parts.length === 0) return null;
  const shown = piecesToGeometry(parts, original);
  if (addedEdit) return { ...addedEdit, shown };
  return { source: { type: 'drawn', geojson: shown }, mode: 'replace', shown };
};

interface RemoveOriginalInput {
  /** The area as it opened. */
  original: Polygon | MultiPolygon;
  /** The drawn parts. */
  pieces: Pieces;
  setPieces: (pieces: Pieces) => void;
  pushHistory: () => void;
  /** Whether the drawn parts differ from the opened shape. */
  drawnChanged: boolean;
  /** How many parts the survey rows or the file add. */
  addedCount: number;
}

/**
 * Remove original area: cuts the opened shape out of the drawn parts, so the
 * shapes drawn beside or over it stay, however many. While the parts are as
 * opened, only survey rows or a file add to them, and removing all the parts
 * leaves those.
 *
 * @param {RemoveOriginalInput} input - The opened shape and the drawn parts.
 * @return {object} Whether anything would stay, and the removal.
 */
function useRemoveOriginal({
  original,
  pieces,
  setPieces,
  pushHistory,
  drawnChanged,
  addedCount,
}: RemoveOriginalInput): Pick<
  EditorSession,
  'canRemoveOriginal' | 'removeOriginal'
> {
  const withoutOriginal = useMemo(
    () => (drawnChanged ? subtractGeometry(pieces, original) : []),
    [drawnChanged, pieces, original]
  );
  // Memoised: the session re-renders on changes that leave the parts alone (a
  // preview landing, a file's status), and re-serializing both shapes each time is
  // wasted on a large area.
  const cutChangesParts = useMemo(
    () =>
      drawnChanged &&
      JSON.stringify(withoutOriginal) !== JSON.stringify(pieces),
    [drawnChanged, withoutOriginal, pieces]
  );
  const canRemoveOriginal = drawnChanged
    ? cutChangesParts && (withoutOriginal.length > 0 || addedCount > 0)
    : pieces.length > 0 && addedCount > 0;

  return {
    canRemoveOriginal,
    removeOriginal: () => {
      pushHistory();
      setPieces(withoutOriginal);
    },
  };
}

interface AddedEditInput {
  area: AreaGeometry;
  workspace: AreaWorkspace;
  surveyRows: SurveyRow[];
  upload: GeometryUpload;
  /** The drawn parts. */
  pieces: Pieces;
  /** Whether the drawn parts differ from the opened shape. */
  drawnChanged: boolean;
}

/**
 * The wire source for what adds to the drawn parts: a verified file, else the
 * located survey rows, else nothing.
 *
 * @param {SurveyRow[]} surveyRows - The survey rows.
 * @param {GeometryUpload} upload - The uploaded file.
 * @return {Record<string, unknown> | null} The source, or null.
 */
const addedSourceFor = (
  surveyRows: SurveyRow[],
  upload: Pick<GeometryUpload, 'result' | 'fileSignedIds'>
): Record<string, unknown> | null => {
  const inputs = {
    surveyRows,
    drawnPolygons: [],
    selectedAreas: [],
    boundaryStyle: 'exact' as const,
    bufferFt: '',
  };
  if (upload.result) {
    return buildAreaSource({
      ...inputs,
      method: 'upload',
      upload: {
        result: upload.result,
        fileSignedIds: upload.fileSignedIds,
      },
    });
  }
  return surveyRows.some((row) => row.location)
    ? buildAreaSource({ ...inputs, method: 'survey' })
    : null;
};

/**
 * Whether a preview's answer is a shape the editor can draw.
 *
 * @param {Geometry | null | undefined} geometry - The answer.
 * @return {boolean} Whether it is a polygon or a multi-part one.
 */
const isAreaShape = (
  geometry: Geometry | null | undefined
): geometry is Polygon | MultiPolygon =>
  geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon';

/**
 * Why the added shape wouldn't save. The preview runs the save's own rules, so rows
 * or a file it refuses would be refused on Save too: Save waits until they are
 * changed or removed. A preview that failed outright says nothing of them, but
 * leaves no shape to save.
 *
 * @param {AreaPreview} preview - The added shape's preview.
 * @return {string[]} The reasons.
 */
const addedErrorsFor = (preview: AreaPreview): string[] => [
  ...preview.errors.map(capitalizeFirstLetter),
  ...(preview.failed
    ? ['The added shape could not be checked. Change it or try again.']
    : []),
];

/**
 * The server's measure of each part, whenever the parts are a shape it built: a
 * survey or upload preview, or the area as opened. Drawn edits keep the client's
 * estimate.
 *
 * @param {object} input - The preview, whether it is the edit, and the area.
 * @return {AreaGeometryPart[] | null} The measures, or null.
 */
const serverFigures = ({
  preview,
  previewIsEdit,
  untouched,
  areaParts,
}: {
  preview: AreaPreview;
  previewIsEdit: boolean;
  untouched: boolean;
  areaParts: AreaGeometryPart[] | null | undefined;
}): AreaGeometryPart[] | null => {
  if (previewIsEdit && preview.current && preview.parts) return preview.parts;
  return untouched ? (areaParts ?? null) : null;
};

/**
 * What the survey rows or a verified file add, and what Save then sends.
 *
 * Either is an edit of its own while the drawn parts are as opened (`add`) or
 * all removed (`replace`): the server builds and composes it, so the preview is
 * the shape the save stores. Once the parts are redrawn too, the added shape
 * alone is previewed and joins the drawn parts, and the save is one drawn shape —
 * a mixed edit's lineage collapses to drawn. The rows and the file never stand
 * together: each replaces the other.
 *
 * @param {AddedEditInput} input - The session's area, rows, file and drawn parts.
 * @return {object} The added parts, the parts on the map, and the edit to save.
 */
function useAddedEdit({
  area,
  workspace,
  surveyRows,
  upload,
  pieces,
  drawnChanged,
}: AddedEditInput) {
  const located = surveyRows.filter((row) => row.location);
  // Built once per change of rows or files, so the preview isn't serialized (an
  // uploaded file's whole shape) on every render.
  const { result: uploadResult, fileSignedIds } = upload;
  const addedSource = useMemo(
    () => addedSourceFor(surveyRows, { result: uploadResult, fileSignedIds }),
    [surveyRows, uploadResult, fileSignedIds]
  );
  let addedMode: GeometryEditMode | null = null;
  if (pieces.length === 0) addedMode = 'replace';
  else if (!drawnChanged) addedMode = 'add';
  const previewInput = useMemo(
    () =>
      addedSource
        ? {
            source: addedSource,
            mode: addedMode ?? ('replace' as const),
            area_geometry_id: workspace.detail.areaGeometryId,
            category: area.category,
          }
        : null,
    [addedSource, addedMode, workspace.detail.areaGeometryId, area.category]
  );
  const addedPreview = useAreaPreview(previewInput);
  // Only an answer for the inputs as they are: the last inputs' answer, still
  // showing while a change waits on its own, would draw (and save) a stale shape.
  const addedGeometry =
    addedSource && addedPreview.current ? addedPreview.geometry : null;
  const addedParts = useMemo(
    () => (isAreaShape(addedGeometry) ? geometryToPieces(addedGeometry) : []),
    [addedGeometry]
  );
  const addedCount = addedParts.length;
  // An `add` preview already holds the draft's parts, unioned with the added
  // shape. Beside redrawn parts the added shape is dissolved into them here, as a
  // finished drawing is, so the drawn save never sends overlapping parts.
  const composed = addedMode === 'add' && addedCount > 0;
  const parts = useMemo(() => {
    if (composed) return addedParts;
    if (addedParts.length === 0 || pieces.length === 0) {
      return [...pieces, ...addedParts];
    }
    return mergePieces([...pieces, ...addedParts]);
  }, [composed, pieces, addedParts]);
  // A composed shape takes its holes from the server's answer, as the save does;
  // otherwise the parts left as opened keep the area's own.
  const holesFrom =
    composed && isAreaShape(addedGeometry) ? addedGeometry : area.geometry;
  // The added shape counts once it is in hand: while a file is sent or checked, or
  // its preview is out, Save waits rather than saving the drawn parts without it.
  const addedPending =
    upload.busy || (addedSource !== null && !addedPreview.current);
  const addedErrors = addedSource ? addedErrorsFor(addedPreview) : [];
  const addedEdit =
    addedSource && addedMode ? { source: addedSource, mode: addedMode } : null;

  const figures = serverFigures({
    preview: addedPreview,
    previewIsEdit: addedEdit !== null,
    untouched: !drawnChanged && addedCount === 0,
    areaParts: area.parts,
  });

  return {
    located,
    addedParts,
    addedCount,
    parts,
    holesFrom,
    addedPending,
    addedErrors,
    pendingEdit:
      addedPending || addedErrors.length > 0
        ? null
        : editToSave(parts, addedEdit, holesFrom),
    partFigures: figures?.length === parts.length ? figures : null,
  };
}

/**
 * One editing session, from open to close: the method, tool and snap choices, the
 * working copy with its undo, the survey rows or file that add parts, and the
 * draft save. Mounted per open, so each open starts from the area as it is.
 *
 * @param {AreaGeometry} area - The area being edited.
 * @param {AreaWorkspace} workspace - Its workspace, which holds the draft.
 * @param {GeometryUpload} upload - The upload method's files.
 * @return {EditorSession} Everything the editor reads and does.
 */
export function useEditorSession(
  area: AreaGeometry,
  workspace: AreaWorkspace,
  upload: GeometryUpload
): EditorSession {
  const [method, setMethod] = useState<EditMethod>('free-draw');
  const [tool, setActiveTool] = useState<EditorTool>('move');
  const [snap, setSnap] = useState(true);
  const [landGridOn, setLandGridOn] = useState(false);
  const [surveyRows, setSurveyRows] = useState<SurveyRow[]>(() => [
    emptySurveyRow(),
  ]);
  const { set: setUpload } = upload;
  const draft = useDraftGeometrySave(workspace);
  const [seed] = useState<Pieces>(() => geometryToPieces(area.geometry));
  // Reference Area Layers: which other areas show, and those added by hand. The
  // areas in view of the edited area's type show to start with; the search asks
  // for that type alone, so a crowded view can't crowd them out.
  const [referenceMode, setReferenceMode] =
    useState<ReferenceMode>('same-type');
  const [addedIds, setAddedIds] = useState<number[]>([]);
  const [viewBounds, setViewBounds] = useState<Bounds | null>(null);
  const areasInView = useAreasInView(
    area,
    viewBounds,
    inViewCategory(referenceMode, area.category)
  );
  const { ids: displayedIds, areas: referenceAreas } = useReferenceAreas(
    area,
    referenceMode,
    addedIds,
    areasInView
  );
  const { highlightedIds, toggleHighlight } = useReferenceHighlights();

  // Adding or removing by hand switches to "Selected areas only", seeded with
  // whatever Same type was showing, so nothing else moves.
  const toggleOnMap = useCallback(
    (id: number, onMap: boolean) => {
      const base = referenceMode === 'selected' ? addedIds : displayedIds;
      const rest = base.filter((shown) => shown !== id);
      setAddedIds(onMap ? rest : [...rest, id]);
      setReferenceMode('selected');
    },
    [referenceMode, addedIds, displayedIds]
  );

  const companion = useMemo(
    () => ({
      value: { surveyRows, uploadFiles: upload.files },
      restore: (value: EditorCompanion) => {
        setSurveyRows(value.surveyRows);
        setUpload(value.uploadFiles);
      },
    }),
    [surveyRows, upload.files, setUpload]
  );
  const edit = useUnitShapeEdit<EditorCompanion>(seed, companion);
  const { pushHistory } = edit;

  // Undoing back to the opened shape leaves nothing to save, so the drawn parts
  // count as changed only while they differ from it.
  const drawnChanged = useMemo(
    () => edit.dirty && JSON.stringify(edit.pieces) !== JSON.stringify(seed),
    [edit.dirty, edit.pieces, seed]
  );

  const {
    located,
    addedParts,
    addedCount,
    parts,
    holesFrom,
    addedPending,
    addedErrors,
    pendingEdit,
    partFigures,
  } = useAddedEdit({
    area,
    workspace,
    surveyRows,
    upload,
    pieces: edit.pieces,
    drawnChanged,
  });

  // Row changes are edits too, so each one is a step to undo, and they take the
  // place of any file.
  const setRowsWithHistory: Dispatch<SetStateAction<SurveyRow[]>> = useCallback(
    (next) => {
      pushHistory();
      setSurveyRows(next);
      setUpload([]);
    },
    [pushHistory, setUpload]
  );
  const surveyPick = useSurveyAreaPick(surveyRows, setRowsWithHistory);

  // So are files, which join any before them and take the place of the rows.
  const { add: addUpload, remove: removeUpload } = upload;
  useFrameUpload(upload.result);
  const addUploadFiles = useCallback(
    (files: File[]) => {
      pushHistory();
      setSurveyRows([emptySurveyRow()]);
      addUpload(files);
    },
    [pushHistory, addUpload]
  );
  const removeUploadFile = useCallback(
    (localId: string) => {
      pushHistory();
      removeUpload(localId);
    },
    [pushHistory, removeUpload]
  );

  /**
   * Removes one part. The added parts are folded into the drawn ones
   * first, as the mock does, so what remains stays exactly as shown.
   *
   * @param {number} index - The part's row.
   */
  const removePart = (index: number) => {
    if (parts.length <= 1) return;
    pushHistory();
    edit.setPieces(parts.filter((_, i) => i !== index));
    setSurveyRows([emptySurveyRow()]);
    setUpload([]);
  };

  /**
   * Takes a shape the circle or polygon tool just finished: it dissolves into
   * every part it overlaps, added parts included, which then fold
   * into the drawn ones, as the mock does. The tool has already stepped history.
   *
   * @param {Pieces} drawn - The drawn parts, with the new or changed shape.
   */
  const commitShapes = (drawn: Pieces) => {
    edit.setPieces(mergePieces([...drawn, ...addedParts]));
    if (addedCount > 0) {
      setSurveyRows([emptySurveyRow()]);
      setUpload([]);
    }
  };

  /**
   * Picks a map tool. From Area from Survey it switches to User-drawn first, so
   * the tool works straight away.
   *
   * @param {EditorTool} next - The tool.
   */
  const setTool = (next: EditorTool) => {
    if (method === 'survey-description') setMethod('free-draw');
    setActiveTool(next);
  };

  const { canRemoveOriginal, removeOriginal } = useRemoveOriginal({
    original: area.geometry,
    pieces: edit.pieces,
    setPieces: edit.setPieces,
    pushHistory,
    drawnChanged,
    addedCount,
  });

  const toolsEnabled = editorToolsEnabled(method, draft.saving);
  const trace = useTraceBoundaries(
    viewBounds,
    toolsEnabled ? tool : null,
    landGridOn
  );

  return {
    method,
    setMethod,
    tool,
    setTool,
    snap,
    setSnap,
    surveyRows,
    setSurveyRows: setRowsWithHistory,
    surveyPick,
    pickedSurveyIds: located.map((row) => row.location?.id ?? 0),
    upload,
    addUploadFiles,
    removeUploadFile,
    edit,
    parts,
    addedCount,
    holesFrom,
    addedPending,
    dirty: drawnChanged || addedCount > 0,
    pendingEdit,
    partFigures,
    addedErrors,
    removePart,
    canRemoveOriginal,
    removeOriginal,
    commitShapes,
    draft,
    toolsEnabled,
    referenceMode,
    setReferenceMode,
    addedIds,
    setAddedIds,
    referenceAreas,
    displayedIds,
    toggleOnMap,
    viewBounds,
    setViewBounds,
    areasInView,
    traceBoundaries: trace.sources,
    landGridOn,
    setLandGridOn,
    landGridZoomIn: trace.landGridZoomIn,
    highlightedIds,
    toggleHighlight,
  };
}

/** How many areas a reference search loads. */
export const REFERENCE_LIMIT = 50;

/**
 * The widest view, in degrees, whose county and state lines Trace loads; the
 * server answers nothing wider (Query::MapBoundaries::MAX_SPAN_DEGREES).
 */
const TRACE_BOUNDARY_SPAN = 10;

/** A county or state as `POST /queries/map_boundaries` returns it. */
interface MapBoundaryRow {
  id: number;
  geometry: Polygon | MultiPolygon;
}

/**
 * The county and state lines in a view, as a query.
 *
 * @param {Bounds | null} bounds - The view.
 * @return {object} The query options.
 */
const mapBoundariesQuery = (bounds: Bounds | null) =>
  queryOptions({
    queryKey: ['area-geometry-editor', 'map-boundaries', bounds],
    queryFn: ({ signal }) =>
      api
        .post('queries/map_boundaries', {
          json: { query: { boundingBox: bounds && toBoundingBox(bounds) } },
          signal,
        })
        .json<{ counties: MapBoundaryRow[]; states: MapBoundaryRow[] }>(),
    staleTime: 5 * 60_000,
  });

/**
 * Whether a view is no wider or taller than a span, in degrees.
 *
 * @param {Bounds | null} bounds - The view.
 * @param {number} span - The span.
 * @return {boolean} True when it fits.
 */
const fitsSpan = (bounds: Bounds | null, span: number): boolean =>
  bounds !== null &&
  bounds[2] - bounds[0] <= span &&
  bounds[3] - bounds[1] <= span;

/**
 * The widest view, in degrees, whose land grid Trace loads; the server answers
 * nothing wider (Query::SurveyBoundaries::MAX_SPAN_DEGREES).
 */
const LAND_GRID_SPAN = 0.5;

/**
 * The side of a land grid tile, in degrees. Most tiles of the dense Texas survey
 * grid come in under the server's 500-section cap at this size (Martin County ran
 * 280 to 500); one that doesn't is quartered (splitDenseTiles).
 */
const LAND_GRID_TILE = 0.2;

const NO_TILES: Bounds[] = [];

/**
 * The fixed-grid tiles covering a view, so the same tile is asked for, and cached,
 * whichever view it falls in.
 *
 * @param {Bounds | null} bounds - The view.
 * @return {Bounds[]} The tiles.
 */
const landGridTiles = (bounds: Bounds | null): Bounds[] => {
  if (!bounds) return NO_TILES;
  /**
   * A tile edge, rounded so float steps never make two keys for one tile.
   *
   * @param {number} index - The edge's index on the grid.
   * @return {number} The edge, in degrees.
   */
  const edge = (index: number) => Number((index * LAND_GRID_TILE).toFixed(6));
  const tiles: Bounds[] = [];
  const [west, south, east, north] = bounds;
  for (let x = Math.floor(west / LAND_GRID_TILE); edge(x) < east; x += 1) {
    for (let y = Math.floor(south / LAND_GRID_TILE); edge(y) < north; y += 1) {
      tiles.push([edge(x), edge(y), edge(x + 1), edge(y + 1)]);
    }
  }
  return tiles;
};

/** A survey section as `POST /queries/survey_boundaries` returns it. */
interface SurveySectionRow {
  id: number;
  name: string | null;
  geometry: Polygon | MultiPolygon;
}

/**
 * The land grid's sections in a view, as a query.
 *
 * @param {Bounds | null} bounds - The view.
 * @return {object} The query options.
 */
const surveyBoundariesQuery = (bounds: Bounds | null) =>
  queryOptions({
    queryKey: ['area-geometry-editor', 'survey-boundaries', bounds],
    queryFn: ({ signal }) =>
      api
        .post('queries/survey_boundaries', {
          json: { query: { boundingBox: bounds && toBoundingBox(bounds) } },
          signal,
        })
        .json<SurveyBoundariesResult>(),
    staleTime: 5 * 60_000,
  });

/** A land grid read: its sections, or none and a flag when it held too many. */
interface SurveyBoundariesResult {
  sectionsOmitted: boolean;
  sections: SurveySectionRow[];
}

/** How many times a dense tile is quartered before the map asks to zoom in. */
const LAND_GRID_SPLITS = 2;

/**
 * The tiles to read, each dense tile already answered followed by its quarters,
 * down to LAND_GRID_SPLITS levels. A tile not yet answered is read as it is. The
 * dense tile stays in the list, though it holds no sections, so it stays watched:
 * left unwatched it would drop out of the cache, and its quarters with it.
 *
 * @param {Bounds[]} tiles - The view's tiles.
 * @param {Function} cached - A tile's answer, if it has one.
 * @param {number} depth - How many times these tiles were quartered.
 * @return {Array<{tile: Bounds, depth: number}>} The tiles to read.
 */
const splitDenseTiles = (
  tiles: Bounds[],
  cached: (tile: Bounds) => SurveyBoundariesResult | undefined,
  depth = 0
): { tile: Bounds; depth: number }[] =>
  tiles.flatMap((tile) => {
    if (depth === LAND_GRID_SPLITS || !cached(tile)?.sectionsOmitted) {
      return [{ tile, depth }];
    }
    const [west, south, east, north] = tile;
    const midX = Number(((west + east) / 2).toFixed(6));
    const midY = Number(((south + north) / 2).toFixed(6));
    const quarters: Bounds[] = [
      [west, south, midX, midY],
      [midX, south, east, midY],
      [west, midY, midX, north],
      [midX, midY, east, north],
    ];
    return [{ tile, depth }, ...splitDenseTiles(quarters, cached, depth + 1)];
  });

/**
 * The land grid tiles read so far, as trace sources, and which tiles answered too
 * dense. The flags are what change when a dense tile answers, which carries no
 * sections, so they are what makes the hook look again and split it.
 *
 * @param {Array<{data?: object}>} results - Each tile's query.
 * @return {{landGrid: TraceSource[], dense: boolean[]}} The sections and flags.
 */
const combineLandGridTiles = (
  results: { data?: SurveyBoundariesResult }[]
): { landGrid: TraceSource[]; dense: boolean[] } => ({
  dense: results.map(({ data }) => data?.sectionsOmitted === true),
  // A section crossing a tile edge comes back from each tile it meets.
  landGrid: results.flatMap(({ data }, index) =>
    (data?.sections ?? []).map((row) => ({
      key: `section-${row.id}-${index}`,
      geometry: row.geometry,
    }))
  ),
});

/** What Trace follows besides the shown areas. */
export interface TraceBoundaries {
  /** County and state lines, plus the land grid's sections when it is on. */
  sources: TraceSource[];
  /** The land grid is on but the view holds too much of it to trace. */
  landGridZoomIn: boolean;
}

/**
 * The county and state lines in the edit map's view, and the land grid's
 * sections when that toggle is on, for Trace to follow along with the shown
 * areas. Loaded only while Trace is the tool, and only for a view narrow enough
 * to trace in: the land grid is far denser, so its view is far narrower.
 *
 * @param {Bounds | null} bounds - The view.
 * @param {EditorTool | null} tool - The tool in use; null while the tools stand down.
 * @param {boolean} landGridOn - Whether the land grid is a trace layer.
 * @return {TraceBoundaries} The boundaries.
 */
export function useTraceBoundaries(
  bounds: Bounds | null,
  tool: EditorTool | null,
  landGridOn: boolean
): TraceBoundaries {
  const enabled = tool === 'trace';
  const gridEnabled = enabled && landGridOn;
  const gridNarrow = fitsSpan(bounds, LAND_GRID_SPAN);
  const { data } = useQuery({
    ...mapBoundariesQuery(bounds),
    enabled: enabled && fitsSpan(bounds, TRACE_BOUNDARY_SPAN),
    // Each pan is a new view; keep the last view's lines until it lands.
    placeholderData: keepPreviousData,
  });
  // The grid is read a tile at a time: a whole view at working zoom holds more
  // sections than the server answers in one go, a tile never does, and a tile
  // already read stays cached as the view pans across it.
  const queryClient = useQueryClient();
  const tiles = useMemo(
    () => (gridEnabled && gridNarrow ? landGridTiles(bounds) : NO_TILES),
    [bounds, gridEnabled, gridNarrow]
  );
  // A tile the server found too dense is read again as its quarters. Worked out
  // from the cache each render, so a split follows as soon as the tile answers.
  const leaves = splitDenseTiles(tiles, (tile) =>
    queryClient.getQueryData(surveyBoundariesQuery(tile).queryKey)
  );
  const { landGrid, dense } = useQueries({
    queries: leaves.map(({ tile }) => surveyBoundariesQuery(tile)),
    combine: combineLandGridTiles,
  });
  // Still too dense after the last split: only zooming in will do.
  const gridOmitted = leaves.some(
    ({ depth }, index) => depth === LAND_GRID_SPLITS && dense[index]
  );
  const sources = useMemo(
    () =>
      data && enabled
        ? [
            ...data.counties.map((row) => ({
              key: `county-${row.id}`,
              geometry: row.geometry,
            })),
            ...data.states.map((row) => ({
              key: `state-${row.id}`,
              geometry: row.geometry,
            })),
            ...landGrid,
          ]
        : landGrid,
    [data, enabled, landGrid]
  );
  return {
    sources,
    landGridZoomIn: gridEnabled && (!gridNarrow || gridOmitted),
  };
}

/** A search row as `POST /queries/area_geometries` returns it. */
interface AreaSearchRow {
  id: number;
  businessId?: string;
  name: string;
  displayName?: string | null;
  category: string;
  grossAcres: number;
  /** Corner to corner; null until the area is indexed. */
  boundingBox?: BoundingBox | null;
}

/** A box as the area search takes and returns it: south-west to north-east. */
interface BoundingBox {
  swLng: number;
  swLat: number;
  neLng: number;
  neLat: number;
}

/**
 * A [west, south, east, north] extent as the search's box.
 *
 * @param {Bounds} bounds - The extent.
 * @return {BoundingBox} The box.
 */
const toBoundingBox = ([swLng, swLat, neLng, neLat]: Bounds): BoundingBox => ({
  swLng,
  swLat,
  neLng,
  neLat,
});

/**
 * Searches the area catalogue: by name for "Search areas", or by type for the
 * reference modes.
 *
 * @param {Record<string, unknown>} query - The search fields.
 * @param {AbortSignal} signal - Cancels a superseded search.
 * @return {Promise<ReferenceCandidate[]>} The rows.
 */
const searchAreas = async (
  query: Record<string, unknown>,
  signal: AbortSignal
): Promise<ReferenceCandidate[]> => {
  const body = await api
    .post('queries/area_geometries', {
      json: { query: { page: 1, pageSize: REFERENCE_LIMIT, ...query } },
      signal,
    })
    .json<{ results: AreaSearchRow[] }>();
  return body.results.map((row) => ({
    id: row.id,
    businessId: row.businessId,
    name: row.displayName || row.name,
    category: row.category,
    grossAcres: row.grossAcres,
    extent: row.boundingBox
      ? [
          row.boundingBox.swLng,
          row.boundingBox.swLat,
          row.boundingBox.neLng,
          row.boundingBox.neLat,
        ]
      : null,
  }));
};

/**
 * The catalogue search, as a query.
 *
 * @param {Record<string, unknown>} query - The search fields.
 * @return {object} The query options.
 */
const areaSearchQuery = (query: Record<string, unknown>) =>
  queryOptions({
    queryKey: ['area-geometry-editor', 'search', query],
    queryFn: ({ signal }) => searchAreas(query, signal),
    staleTime: 60_000,
  });

/**
 * One area's geometry, as a query.
 *
 * @param {number} id - The area.
 * @return {object} The query options.
 */
const areaGeometryQuery = (id: number) =>
  queryOptions({
    queryKey: ['area-geometry-editor', 'geometry', id],
    queryFn: ({ signal }) =>
      api.get(`area_geometries/${id}`, { signal }).json<AreaGeometry>(),
    staleTime: 5 * 60_000,
  });

/** The edited area, as the searches leave it out. */
type EditedArea = Pick<AreaGeometry, 'id' | 'businessId'>;

/**
 * Whether a search row is not the edited area. The draft is a clone with its own
 * id, so the search finds the published version of the same area by its
 * business id instead.
 *
 * @param {ReferenceCandidate} row - The row.
 * @param {EditedArea} edited - The area being edited.
 * @return {boolean} True for another area.
 */
const isOtherArea = (row: ReferenceCandidate, edited: EditedArea): boolean =>
  row.id !== edited.id &&
  !(edited.businessId && row.businessId === edited.businessId);

/**
 * "Search areas": the catalogue matching what is typed, less the edited area.
 *
 * @param {string} text - What is typed.
 * @param {EditedArea} edited - The area being edited.
 * @return {{ candidates: ReferenceCandidate[], loading: boolean }} The matches.
 */
export function useReferenceSearch(
  text: string,
  edited: EditedArea
): { candidates: ReferenceCandidate[]; loading: boolean } {
  const query = text.trim();
  const { data, isFetching } = useQuery({
    ...areaSearchQuery({ text: query }),
    enabled: query !== '',
  });
  return {
    candidates: (data ?? []).filter((row) => isOtherArea(row, edited)),
    loading: isFetching,
  };
}

/**
 * The areas the edit map shows for reference, with their geometry: the ones added
 * by hand, or those in view of the edited area's type.
 *
 * @param {AreaGeometry} area - The area being edited.
 * @param {ReferenceMode} mode - Show areas.
 * @param {number[]} addedIds - Areas added in "Selected areas only".
 * @param {ReferenceCandidate[]} inView - The areas in view, as the mode scopes them.
 * @return {{ ids: number[], areas: ReferenceArea[] }} What is shown, and what has loaded.
 */
export function useReferenceAreas(
  area: AreaGeometry,
  mode: ReferenceMode,
  addedIds: number[],
  inView: ReferenceCandidate[]
): { ids: number[]; areas: ReferenceArea[] } {
  const ids = (
    mode === 'selected' ? addedIds : inView.map((row) => row.id)
  ).filter((id) => id !== area.id);

  const results = useQueries({ queries: ids.map(areaGeometryQuery) });

  const areas = results.flatMap((result) => {
    const loaded = result.data;
    if (!loaded?.geometry) return [];
    return [
      {
        id: loaded.id,
        name: loaded.displayName || loaded.name,
        category: loaded.category,
        grossAcres: loaded.grossAcres,
        geometry: loaded.geometry,
      },
    ];
  });
  return { ids, areas };
}

/**
 * Other areas in the edit map's view, for the Scoped areas list: the search
 * returns those whose boundary meets the view's box.
 *
 * @param {EditedArea} edited - The area being edited.
 * @param {Bounds | null} bounds - The view.
 * @param {string | null} category - Only this type, for "Same type"; null for every type.
 * @return {ReferenceCandidate[]} The areas in view.
 */
export function useAreasInView(
  edited: EditedArea,
  bounds: Bounds | null,
  category: string | null = null
): ReferenceCandidate[] {
  const { data } = useQuery({
    ...areaSearchQuery({
      boundingBox: bounds && toBoundingBox(bounds),
      ...(category ? { category } : {}),
    }),
    enabled: bounds !== null,
    // Each pan is a new search; keep the last view's rows until it lands.
    placeholderData: keepPreviousData,
  });
  const { id, businessId } = edited;
  return useMemo(
    () => (data ?? []).filter((row) => isOtherArea(row, { id, businessId })),
    [data, id, businessId]
  );
}

/**
 * Frames the uploaded shape whenever it changes, since a file can land far from
 * the area being edited. Keyed on its extent, so a re-read of the same files
 * leaves the user's view alone.
 *
 * @param {VerifiedUpload | null} result - The verified files' shape, joined.
 */
function useFrameUpload(result: VerifiedUpload | null) {
  const mapRef = useAtomValue(editorMapRefAtom);
  const bounds = useMemo(
    () => (result ? piecesBounds(geometryToPieces(result.geojson)) : null),
    [result]
  );
  const key = bounds ? bounds.join(',') : null;

  useEffect(() => {
    if (!mapRef || !bounds) return;
    mapRef.fitBounds(bounds, { padding: 80, duration: 600 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a new result with the same extent keeps the view.
  }, [mapRef, key]);
}

/**
 * Show on map: outlines a reference row and frames it on the map, in place of
 * any row outlined before; again clears its outline.
 *
 * @return {object} The outlined ids and the toggle.
 */
function useReferenceHighlights(): Pick<
  EditorSession,
  'highlightedIds' | 'toggleHighlight'
> {
  const [highlightedIds, setHighlightedIds] = useState<number[]>([]);
  const mapRef = useAtomValue(editorMapRefAtom);
  const queryClient = useQueryClient();

  const toggleHighlight = useCallback(
    (candidate: ReferenceCandidate) => {
      if (highlightedIds.includes(candidate.id)) {
        setHighlightedIds([]);
        return;
      }
      setHighlightedIds([candidate.id]);
      /**
       * Frames the area once its extent is known.
       *
       * @param {Bounds | null | undefined} extent - West, south, east, north.
       */
      const frame = (extent: Bounds | null | undefined) => {
        if (extent) mapRef?.fitBounds(extent, { padding: 80, duration: 600 });
      };
      if (candidate.extent) {
        frame(candidate.extent);
        return;
      }
      queryClient
        .fetchQuery(areaGeometryQuery(candidate.id))
        .then((loaded) =>
          frame(
            loaded.geometry && piecesBounds(geometryToPieces(loaded.geometry))
          )
        )
        .catch(() => {
          // The outline still loads; the map just doesn't move.
        });
    },
    [highlightedIds, mapRef, queryClient]
  );
  return { highlightedIds, toggleHighlight };
}

/**
 * The geometries of the rows put on the map with Show on map, for their outlines.
 *
 * @param {number[]} ids - The highlighted areas.
 * @return {Array<Polygon | MultiPolygon>} Those loaded so far.
 */
export function useHighlightGeometries(
  ids: number[]
): (Polygon | MultiPolygon)[] {
  const results = useQueries({ queries: ids.map(areaGeometryQuery) });
  return results.flatMap((result) =>
    result.data?.geometry ? [result.data.geometry] : []
  );
}

/**
 * The width the map and tools share: the row, less the splitter between them.
 *
 * @param {HTMLDivElement | null} row - The map-and-tools row.
 * @return {number | null} The width in px, or null before it renders.
 */
const sharedWidth = (row: HTMLDivElement | null): number | null =>
  row ? row.clientWidth - TOOLS_WIDTH.splitter : null;

/**
 * The tools column's width, set by the splitter: remembered between opens in
 * this browser, and clamped so the map keeps its minimum.
 *
 * @param {RefObject<HTMLDivElement | null>} columnsRef - The map-and-tools row.
 * @return {object} The width, a setter that clamps, and a reset to the default.
 */
export function useToolsWidth(columnsRef: RefObject<HTMLDivElement | null>) {
  const [width, setRawWidth] = useState(() => {
    try {
      const stored = Number(
        window.localStorage.getItem(TOOLS_WIDTH.storageKey)
      );
      return stored >= TOOLS_WIDTH.min ? stored : TOOLS_WIDTH.initial;
    } catch {
      return TOOLS_WIDTH.initial;
    }
  });

  const setWidth = useCallback(
    (next: number) =>
      setRawWidth(
        clampToolsWidth(next, sharedWidth(columnsRef.current) ?? Infinity)
      ),
    [columnsRef]
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(TOOLS_WIDTH.storageKey, String(width));
    } catch {
      // A blocked store only means the width isn't remembered.
    }
  }, [width]);

  // A width remembered on a wider screen may not fit this one.
  useEffect(() => {
    const total = sharedWidth(columnsRef.current);
    if (!total) return;
    const fitted = clampToolsWidth(width, total);
    if (fitted !== width) setRawWidth(fitted);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fits once on open; drags clamp as they go
  }, []);

  const reset = useCallback(() => setWidth(TOOLS_WIDTH.initial), [setWidth]);

  return { width, setWidth, reset };
}

/**
 * Esc closes a popover open inside the editor (the drawing tools' help) rather
 * than the editor. The modal hears Esc on the document in the capture phase and
 * closes, so this listens a step earlier, on the window, as the draw tools do,
 * and only while the popover is open.
 *
 * @param {RefObject<HTMLElement | null>} ref - Wraps the popover's toggle.
 */
export function useEscapeClosesPopover(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    /**
     * Closes the open popover instead of the editor.
     *
     * @param {KeyboardEvent} event - The key press.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const toggle = ref.current?.querySelector<HTMLButtonElement>(
        'button[aria-expanded="true"]'
      );
      if (!toggle) return;
      event.stopPropagation();
      toggle.click();
      toggle.focus();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [ref]);
}
